import React, { useState } from 'react';

const DeleteAccount = () => {
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    appType: 'Fleet / CRM App' 
  });

  const [errors, setErrors] = useState({
    name: '',
    mobile: '',
    email: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault(); // Prevents layout flashing / page reload

    const currentErrors = { name: '', mobile: '', email: '' };
    let isValid = true;

    // India-Specific Name Validation
    const cleanName = formData.name.trim();
    const nameRegex = /^[a-zA-Z\s.]+$/; 

    if (cleanName === '') {
      currentErrors.name = 'Full name is required.';
      isValid = false;
    } else if (cleanName.length < 3) {
      currentErrors.name = 'Please enter your full official name as per KYC profiles (minimum 3 characters).';
      isValid = false;
    } else if (!nameRegex.test(cleanName)) {
      currentErrors.name = 'Name should only contain letters, spaces, or dots.';
      isValid = false;
    }

    // India-Specific Mobile Validation
    const cleanMobile = formData.mobile.trim().replace(/\s+/g, '');
    const mobileRegex = /^(?:\+91|91|0)?[6-9]\d{9}$/;

    if (cleanMobile === '') {
      currentErrors.mobile = 'Registered mobile number is required.';
      isValid = false;
    } else if (!mobileRegex.test(cleanMobile)) {
      currentErrors.mobile = 'Please enter a valid 10-digit Indian mobile number (e.g., +91 98446 XXXXX).';
      isValid = false;
    }

    // Email Validation
    const emailRegex = /^[^\s@]+@([^\s@.,]+\.)+[^\s@.,]{2,}$/;
    if (formData.email.trim() === '') {
      currentErrors.email = 'Email address is required.';
      isValid = false;
    } else if (!emailRegex.test(formData.email.trim())) {
      currentErrors.email = 'Please enter a valid email address (e.g., name@domain.com).';
      isValid = false;
    }

    setErrors(currentErrors);

    if (isValid) {
      setIsSubmitting(true);
      setSubmitStatus(null);

      // Web3Forms natively maps standard payload keys to an email layout template.
      // To bypass code printing, we separate the custom form keys from the message container.
      const apiData = {
        access_key: "3a7ba3f3-52cd-4a2c-ae16-bffe86e5a701", // Paste your free access key here
        subject: `Account Deletion Request - ${formData.name}`,
        from_name: "Wowcarz Platform",
        to_email: "admin@wowcarz.in",
        
        // Pass your custom form values directly as separate root keys.
        // Web3Forms will automatically pick these up and render them inside a clean graphical table layout!
        "Full Name": formData.name,
        "Mobile Number": formData.mobile,
        "Email Address": formData.email,
        "App / Account Type": formData.appType,
        
        // This acts as a short summary text block above the structured data table
        message: "A user has requested permanent deletion of their account profile and related operational data."
      };

      try {
        const response = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
          },
          body: JSON.stringify(apiData)
        });

        const result = await response.json();
        if (result.success) {
          setSubmitStatus('success');
          setFormData({ name: '', mobile: '', email: '', appType: 'Fleet / CRM App' });
        } else {
          setSubmitStatus('error');
        }
      } catch (error) {
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <h1 style={styles.h1}>Delete Your Wowcarz Account</h1>
        <p style={styles.p}>
          Please use this form to request the permanent deletion of your Wowcarz Self Drive Car Rental account and all related personal data.
        </p>
        
        {submitStatus === 'success' && (
          <div style={{ backgroundColor: '#d4edda', color: '#155724', padding: '15px', borderRadius: '6px', marginBottom: '20px', fontWeight: '600' }}>
            ✓ Request submitted successfully. Your account deactivation will be processed within 48 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div style={{ backgroundColor: '#f8d7da', color: '#721c24', padding: '15px', borderRadius: '6px', marginBottom: '20px', fontWeight: '600' }}>
            ✕ Submission failed. Please try again or contact support at admin@wowcarz.in directly.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="name">Full Name:</label>
            <input 
              type="text" 
              id="name" 
              placeholder="As registered on your KYC profiles" 
              style={styles.input} 
              value={formData.name}
              onChange={handleInputChange}
              required 
              disabled={isSubmitting}
            />
            {errors.name && <div style={styles.errorText}>{errors.name}</div>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="mobile">Registered Mobile Number:</label>
            <input 
              type="text" 
              id="mobile" 
              placeholder="e.g., +91 98446 XXXXX" 
              style={styles.input} 
              value={formData.mobile}
              onChange={handleInputChange}
              required 
              disabled={isSubmitting}
            />
            {errors.mobile && <div style={styles.errorText}>{errors.mobile}</div>}
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="email">Registered Email Address:</label>
            <input 
              type="email" 
              id="email" 
              placeholder="yourname@domain.com" 
              style={styles.input} 
              value={formData.email}
              onChange={handleInputChange}
              required 
              disabled={isSubmitting}
            />
            {errors.email && <div style={styles.errorText}>{errors.email}</div>}
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="appType">App / Account Type:</label>
            <select 
              id="appType" 
              style={styles.select}
              value={formData.appType}
              onChange={handleInputChange}
              disabled={isSubmitting}
            >
              <option value="Customer App">Wowcarz - Self Drive Car Rental App</option>
              <option value="Fleet / CRM App">Wowcarz Fleet / CRM Platform</option>
            </select>
          </div>
          
          <button 
            type="submit" 
            style={{...styles.button, opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer'}} 
            disabled={isSubmitting}
            onMouseOver={(e) => !isSubmitting && (e.target.style.backgroundColor = '#c92a2a')} 
            onMouseOut={(e) => !isSubmitting && (e.target.style.backgroundColor = '#e03131')}
          >
            {isSubmitting ? 'Processing Request...' : 'Permanently Delete My Account & Data'}
          </button>
        </form>

        <div className="compliance-box" style={styles.complianceBox}>
          <h3>What data is deleted or kept?</h3>
          <ul style={{ paddingLeft: '20px', marginBottom: '10px', color: '#495057', listStyle: 'none' }}>
            <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ color: '#000000', fontSize: '12px', marginTop: '4px' }}>●</span>
              <span><strong>Data Deleted Permanently:</strong> Your account credentials, profile configurations, uploaded identity proofs (Driving License, Aadhaar, Local Address Proofs), and precise GPS/location history details captured via the app.</span>
            </li>
            <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <span style={{ color: '#000000', fontSize: '12px', marginTop: '4px' }}>●</span>
              <span><strong>Data Retained Temporarily:</strong> Historic car booking records, rental transactions, accounting statements, and security deposit details are securely archived for statutory compliance, legal verification, and financial auditing rules under Indian law, after which they are thoroughly purged.</span>
            </li>
          </ul>
          <p style={{ margin: 0, fontSize: '13px', color: '#6c757d' }}>
            Your login capability will be deactivated within 48 hours. Complete data erasure from backend infrastructure will be processed within 14 business days.
          </p>
        </div>
      </div>

      <div className="footer" style={styles.footer}>
        <strong>HCUBE WOWCARZ PRIVATE LIMITED</strong><br />
        Registered Address: No. 1029, 17th Main, 2nd Block, BTM 4th Stage, Bengaluru, Karnataka 560076<br />
        Official Developer Support: admin@wowcarz.in | hcube.app@gmail.com
      </div>
    </div>
  );
};


const styles = {
  body: { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', lineHeight: '1.6', color: '#333', maxWidth: '850px', margin: '60px auto 60px auto', padding: '0 20px', textAlign: 'left' },
  container: { background: '#ffffff', padding: '35px', borderRadius: '8px', border: '1px solid #eaeaea', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' },
  h1: { color: '#111', fontSize: '26px', marginBottom: '8px', fontWeight: '700', textAlign: 'left' },
  p: { color: '#555', fontSize: '15px', marginBottom: '24px', textAlign: 'left' },
  formGroup: { marginBottom: '20px', textAlign: 'left' },
  label: { display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#444', textAlign: 'left' },
  input: { width: '100%', padding: '12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '15px', backgroundColor: '#fafafa', boxSizing: 'border-box' },
  select: { width: '100%', padding: '12px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '15px', backgroundColor: '#fafafa', boxSizing: 'border-box' },
  button: { backgroundColor: '#e03131', color: 'white', border: 'none', padding: '14px 20px', fontSize: '16px', fontWeight: '700', borderRadius: '6px', width: '100%', marginTop: '10px', transition: 'background 0.2s' },
  complianceBox: { backgroundColor: '#f8f9fa', borderLeft: '4px solid #dee2e6', padding: '20px', borderRadius: '0 6px 6px 0', marginTop: '30px', fontSize: '14px', textAlign: 'left' },
  footer: { textAlign: 'center', marginTop: '40px', fontSize: '12px', color: '#868e96', lineHeight: '1.4', paddingBottom: '20px' },
  errorText: { color: '#e03131', fontSize: '12px', marginTop: '4px', textAlign: 'left' }
};

export default DeleteAccount;